'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Navigation item from the sidebar */
interface NavItem {
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: NavItem[];
  hasIndexFile?: boolean;
  indexPath?: string;
  /** Original filesystem path preserving casing (for MCP agent references) */
  originalPath?: string;
  /** Original index path preserving casing (for MCP agent references) */
  originalIndexPath?: string;
}

/** Flattened navigable page (file or folder with index) */
interface NavigablePage {
  path: string;
  name: string;
}

/** Flatten nav tree into ordered list of navigable pages */
function flattenNavigablePages(items: NavItem[], basePrefix: string): NavigablePage[] {
  const pages: NavigablePage[] = [];

  const walk = (nodes: NavItem[]) => {
    for (const node of nodes) {
      if (node.type === 'file') {
        // Regular file - always navigable
        pages.push({
          path: `${basePrefix}/${node.path}`,
          name: node.name,
        });
      } else if (node.type === 'folder') {
        // Folder - navigable to index if exists, otherwise to statistics page
        if (node.hasIndexFile && node.indexPath) {
          // Has index → navigate to index file
          pages.push({
            path: `${basePrefix}/${node.indexPath}`,
            name: node.name,
          });
        } else {
          // No index → navigate to folder (statistics page)
          pages.push({
            path: `${basePrefix}/${node.path}`,
            name: node.name,
          });
        }
        // Recurse into children
        if (node.children && node.children.length > 0) {
          walk(node.children);
        }
      }
    }
  };

  walk(items);
  return pages;
}

// Global storage for nav items (shared between VaultLeftPane and VaultMain via events)
let globalNavItems: NavItem[] = [];
let globalBasePrefix = '';
let globalCurrentVaultSlug = '';
let globalCurrentOrgSlug: string | null = null;

/** Vault info for breadcrumb navigation */
export interface VaultInfo {
  slug: string;
  title: string;
  href: string;
}

// Global storage for vault list (populated by NavCenterContent)
let globalVaults: VaultInfo[] = [];

/** Dispatch nav items update event */
export function dispatchNavItemsUpdate(items: NavItem[], basePrefix: string, vaultSlug?: string, orgSlug?: string | null) {
  globalNavItems = items;
  globalBasePrefix = basePrefix;
  if (vaultSlug) {
    globalCurrentVaultSlug = vaultSlug;
  }
  // Store org slug (can be null for platform-level vaults)
  globalCurrentOrgSlug = orgSlug ?? null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('vault:nav-items-updated'));
  }
}

/** Dispatch vault list update event (called by NavCenterContent when vault data is available) */
export function dispatchVaultsUpdate(vaults: VaultInfo[]) {
  globalVaults = vaults;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('vault:vaults-updated'));
  }
}

/** Get the current vault slug */
export function getCurrentVaultSlug(): string {
  return globalCurrentVaultSlug;
}

/** Get the current organization slug (null for platform-level vaults) */
export function getCurrentOrgSlug(): string | null {
  return globalCurrentOrgSlug;
}

/** Get the list of vaults for the current organization */
export function getVaults(): VaultInfo[] {
  return globalVaults;
}

/** Sibling item for dropdown navigation */
export interface SiblingItem {
  name: string;
  path: string;
  href: string;
  type: 'file' | 'folder' | 'vault';
  hasIndexFile?: boolean;
}

/**
 * Get vault siblings (other vaults in the organization).
 * Used when clicking on the vault name in the breadcrumb.
 */
export function getVaultSiblings(): SiblingItem[] {
  return globalVaults.map((vault) => ({
    name: vault.title,
    path: vault.slug,
    href: vault.href,
    type: 'vault' as const,
  }));
}

/**
 * Get siblings at a given level in the nav tree.
 * @param segmentPath - Path to the current segment (e.g., "folder" or "folder/subfolder")
 * @returns Array of siblings at that level, including the current item
 */
export function getSiblingsAtPath(segmentPath: string): SiblingItem[] {
  if (!segmentPath) {
    // Root level (vault root) - return top-level items in the vault
    return globalNavItems.map((item) => ({
      name: item.name,
      path: item.path,
      href: `${globalBasePrefix}/${item.hasIndexFile && item.indexPath ? item.indexPath : item.path}`,
      type: item.type,
      hasIndexFile: item.hasIndexFile,
    }));
  }

  // Find the parent path by removing the last segment
  const lastSlash = segmentPath.lastIndexOf('/');
  const parentPath = lastSlash > 0 ? segmentPath.slice(0, lastSlash) : '';

  // Walk the tree to find the parent folder's children (siblings of the current item)
  const findChildren = (items: NavItem[], targetPath: string): NavItem[] | null => {
    if (!targetPath) {
      // Parent is root level - return root items
      return items;
    }
    for (const item of items) {
      if (item.type === 'folder') {
        // Check if this folder's path matches the target parent
        if (item.path.toLowerCase() === targetPath.toLowerCase()) {
          return item.children ?? [];
        }
        // Check children recursively if target is deeper
        if (item.children && targetPath.toLowerCase().startsWith(item.path.toLowerCase() + '/')) {
          const result = findChildren(item.children, targetPath);
          if (result) return result;
        }
      }
    }
    return null;
  };

  const siblings = findChildren(globalNavItems, parentPath);

  if (!siblings) {
    return [];
  }

  return siblings.map((item) => ({
    name: item.name,
    path: item.path,
    href: `${globalBasePrefix}/${item.hasIndexFile && item.indexPath ? item.indexPath : item.path}`,
    type: item.type,
    hasIndexFile: item.hasIndexFile,
  }));
}

/** Get the first navigable page in a folder (index or first child file) */
export function getFirstNavigablePage(folderPath: string): string | null {
  const findFolder = (items: NavItem[], path: string): NavItem | null => {
    const segments = path.split('/').filter(Boolean);
    let current = items;
    for (const segment of segments) {
      const found = current.find((item) => item.name.toLowerCase() === segment.toLowerCase());
      if (!found) return null;
      if (found.type === 'folder' && found.children) {
        current = found.children;
      } else {
        return found;
      }
    }
    // Found the folder - return a mock NavItem representing the folder's first child
    if (current.length > 0) {
      const firstFile = current.find((item) => item.type === 'file');
      if (firstFile) return firstFile;
      // Check for folders with index
      const folderWithIndex = current.find((item) => item.type === 'folder' && item.hasIndexFile);
      if (folderWithIndex) return folderWithIndex;
    }
    return null;
  };

  // First check if the folder itself has an index
  const folder = globalNavItems.find((item) => item.path.toLowerCase() === folderPath.toLowerCase());
  if (folder?.hasIndexFile && folder.indexPath) {
    return `${globalBasePrefix}/${folder.indexPath}`;
  }

  // Otherwise find first navigable child
  const firstChild = findFolder(globalNavItems, folderPath);
  if (firstChild) {
    if (firstChild.hasIndexFile && firstChild.indexPath) {
      return `${globalBasePrefix}/${firstChild.indexPath}`;
    }
    return `${globalBasePrefix}/${firstChild.path}`;
  }

  return null;
}

/** Hook to get previous/next page navigation */
export function useVaultPageNavigation() {
  const [navigablePages, setNavigablePages] = useState<NavigablePage[]>([]);
  const basePrefixRef = useRef(globalBasePrefix);

  // Listen for nav items updates
  useEffect(() => {
    const handleUpdate = () => {
      basePrefixRef.current = globalBasePrefix;
      setNavigablePages(flattenNavigablePages(globalNavItems, globalBasePrefix));
    };

    // Initialize with current global state
    handleUpdate();

    window.addEventListener('vault:nav-items-updated', handleUpdate);
    return () => window.removeEventListener('vault:nav-items-updated', handleUpdate);
  }, []);

  const getPreviousPage = useCallback(
    (currentPath: string): NavigablePage | null => {
      const decodedCurrent = decodeURIComponent(currentPath).toLowerCase();
      const currentIndex = navigablePages.findIndex(
        (p) => decodeURIComponent(p.path).toLowerCase() === decodedCurrent
      );
      if (currentIndex > 0) {
        return navigablePages[currentIndex - 1];
      }
      return null;
    },
    [navigablePages]
  );

  const getNextPage = useCallback(
    (currentPath: string): NavigablePage | null => {
      const decodedCurrent = decodeURIComponent(currentPath).toLowerCase();
      const currentIndex = navigablePages.findIndex(
        (p) => decodeURIComponent(p.path).toLowerCase() === decodedCurrent
      );
      if (currentIndex !== -1 && currentIndex < navigablePages.length - 1) {
        return navigablePages[currentIndex + 1];
      }
      return null;
    },
    [navigablePages]
  );

  return { getPreviousPage, getNextPage };
}
